import React, { Fragment, useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import classnames from 'classnames'
import { shallowEqual, useSelector } from 'react-redux'
import HrbTranslate from '../../../../components/language/HrbTranslate'
import ContentPlain from '../../../../components/layout/ContentPlain'
import Button from '../../../../components/library/buttons/Button'
import BlockSignalLink from '../../../../components/library/common/BlockSignalLink'
import { useScrollEvents } from '../../../../hooks/useScrollEvents'
import { useTimer } from '../../../../hooks/useTimer'
import { hasValue } from '../../../../services/util/CommonUtil'
import '../../../../stylesheets/custom_scrollbar.scss'
import { useUnreadMessagesContext } from '../../contexts/UnreadMessagesContext'
import messageService from '../../services/messages/MessageService'
import Divider from './Divider'
import Message from './Message'
import './MessagingWindow.scss';
import MessageInputEditor from './MessageInputEditor'

const MessagingWindow = () => {

    const refreshSeconds = 60
    const messageLimit = 25
    const messageListRef = useRef()

    const { conversationId, setUnreadMessagesCount, setUnreadMessageIds, setMarkAsRead } = useUnreadMessagesContext();

    const [messages, setMessages] = useState(null)
    const [messageContent, setMessageContent] = useState('')
    const [sending, setSending] = useState(false)
    const [hasMoreMessages, setHasMoreMessages] = useState(true)
    const [firstUnreadMessageId, setFirstUnreadMessageId] = useState(null)
    const [loadTrigger, setLoadTrigger] = useState('') // set to 'new' to load new messages, 'older' to retrieve previous ones.

    const { id: ticketId, name: ticketName, targetId: clientId } = useSelector(state => state.tickets.selectedTicket || {}, shallowEqual)
    const taxProId = useSelector(state => state.accountData.employeeId)
    

    const loadMessages = (loadNew) => {
        // get messages from the server
        let offset = loadNew ? 0 : (messages || []).length
        messageService.getMessages(conversationId, offset, messageLimit)
            .then(response => {
                if (response) {
                    let newMessages = []
                    let { items, offset, resultCount, count: totalCount } = response;    

                    // if something was returned, append it and re-filter(distinct) and sort
                    if (items) {
                        newMessages = messageService.filterAndSort(messages ? messages.concat(items) : items)
                    }

                    // if querying older messages, determine whether or not there are any more messages left to fetch
                    if (!loadNew && response.totalCount) {
                        setHasMoreMessages(offset + resultCount < totalCount)
                    }

                    // update the unread messages count
                    determineUnreadMessages(newMessages)

                    // if this is the first load, or if there are new messages in the list, update the state
                    if (!messages || !_.isEqual(messages, newMessages)) {
                        fixScrollAndSetItems(newMessages)
                    }

                    // check for bottom scroll
                    if (loadNew && (isScrolledToTheEnd || !messages)) {
                        scrollToBottom()
                    }
                } 
            })
            .catch(error => console.log(error))
    }

    const determineUnreadMessages = (msgs) => {
        if (msgs) {
            // gets a tally of all unread messages that we know about
            let isRead = _.countBy(msgs, 'isRead')
            setUnreadMessagesCount(isRead.false)

            // find the first unread message in our list
            let firstUnread = msgs.find(m => m.isRead === false)
            setFirstUnreadMessageId(firstUnread ? firstUnread.id : null)

            // grab the unread ids to provide to the UnreadMessagesContext.
            // This is used to set these messages as read when this component is unmounted.
            let unreadIds = messageService.extractUnreadMessageIds(msgs)
            if (unreadIds && unreadIds.length > 0) {
                setUnreadMessageIds(unreadIds)
            }
        }
    }

    const fixScrollAndSetItems = (newMessages) => {
        // get current offset
        let firstChild = messageListRef.current.firstChild
        let currentOffset = getOffset(firstChild).top - messageListRef.current.scrollTop
        // set new items
        setMessages(newMessages)
        // restore scroll to item that was previously at the top
        messageListRef.current.scrollTop = getOffset(firstChild).top - currentOffset
    }

    const sendMessage = async () => {
        if (messageContent && hasValue(conversationId) && conversationId !== -1) {

            setSending(true)

            // mark the rest of the messages as read first
            let readMessages = await markMessagesRead()

            // then perform the send
            let sentMessage = await messageService.createMessage(conversationId, messageContent.trim(), ticketId)
            if (sentMessage) {
                // add sent message to our list and increase our offset value for the next getMessages call.
                let allMessages = readMessages.concat([sentMessage])
                let newMessages = messageService.filterAndSort(allMessages)

                // and of course, now the people demand a recount!
                determineUnreadMessages(newMessages)

                // update message list
                setMessages(newMessages)

                localStorage.removeItem(`unsentMessage_${ticketId}`);

                // clear the message textarea
                setMessageContent('')
                setSending(false)
            }
        }
    }

    const markMessagesRead = async () => {
        let newMessages = messages
        let result = {}

        if (messages) {
            // find all unread messages and their ids
            let unreadIds = messageService.extractUnreadMessageIds(messages)

            // submit those ids to be marked as read server-side
            if (unreadIds && unreadIds.length > 0) {
                result = await messageService.markMessagesRead(unreadIds)
            }

            if (result.status) {
                // now, remap the message list with the new read status
                newMessages = messages.map(m => {
                    if (!m.isRead && m.id <= result.maxMessageId) {
                        return { ...m, isRead: true }
                    }
                    return m
                })
            }
        }

        // finally, bring it on back
        return newMessages
    }

    const [start, stop, isRunning] = useTimer(() => setLoadTrigger('new'), refreshSeconds, false)
    const [isScrolledToTheEnd, scrollToBottom, getOffset] = useScrollEvents(messageListRef, () => { setLoadTrigger('older') })

    useEffect(() => {
        // cleanup function to tell our context to go ahead and mark
        // any known unread messages as read once we've left this page.
        setMarkAsRead(true)
    }, [])

    useEffect(() => {
        if (loadTrigger) {
            // perform the load
            loadMessages(loadTrigger === 'new')
            // reset the trigger
            setLoadTrigger('')
        }
    }, [loadTrigger])

    useEffect(() => {
        // stop timer if it is already running, and reset message list
        if (isRunning) stop()
        setMessages(null)
    }, [ticketId])

    useEffect(() => {
        if (hasValue(conversationId)) {
            // if we successfully retrieved and/or created the conversation, check for membership
            messageService.checkConversationMemberships(taxProId, clientId, conversationId)
                .then(success => {
                    if (success) {
                        // do initial message retrieval
                        setLoadTrigger('new')
                        // start timer
                        if (!isRunning) start()
                    } else {
                        // set messages to an empty object, and give up here.
                        setMessages([])
                    }
                })
                .catch(error => console.log(error))
        }
    }, [conversationId])   

    useEffect(() => {
        // once the message has finished sending, scroll the new message into view.
        if (sending === false) {
            scrollToBottom();
        } else {
            sendMessage()
        }
    }, [sending])

    return (
        <ContentPlain elementId='messaging-pane' className='mt-6 mb-4'>
            <div className='columns px-3 mb-0'>
                <div className='messaging-view card column is-10 is-offset-1 py-5'>
                    <div id='message-list' ref={messageListRef} key='message-container' className={classnames('messages-container custom-scroll', { 'show-scroll': !messages })}>
                        {!hasMoreMessages && messages && messages.length > 0 &&
                            <Divider textKey='Messages.EndOfMessages' />
                        }
                        {messages && conversationId === -1 &&
                            <div className='columns mt-5'>
                                <div className='column is-full'>
                                <HrbTranslate text="Messages.ConversationError" params={{ BlockSignal: <BlockSignalLink /> }} className='error' />
                                </div>
                            </div>
                        }
                        {!messages &&
                            <Fragment>
                                <Message />
                                <Message isClientMessage />
                                <Message />
                            </Fragment>
                        }
                        {messages && messages.map(message => {
                            let isClientMessage = message.participant.userId === clientId
                            let senderName = isClientMessage ? ticketName.en : 'H&R Block' // todo: get real names!
                            return <Fragment key={message.id}>
                                {(message.id === firstUnreadMessageId) && <Divider textKey='Messages.LastRead' />}
                                <Message key={message.id} loaded message={message} senderName={senderName} isClientMessage={isClientMessage} />
                            </Fragment>
                        })}
                    </div>
                </div>
            </div>
            <div className='columns px-3 mt-0'>
                <div className='messaging-input card column is-10 is-offset-1'>
                    <MessageInputEditor
                        ticketId={ticketId}
                        onInputChanged={setMessageContent}
                        messageContent={messageContent}
                        disabled={sending || conversationId === -1} />
                    <div className='send-button-container columns mt-4 mb-0 mx-6 px-6'>
                        <Button elementId='btn-send-message'
                            labelKey='Messages.SendMessage'
                            disabled={!(messageContent.replaceAll('<p>', '').replaceAll('</p>', '').replaceAll('<br>', '').replaceAll('<strong>', '').replaceAll('</strong>', '').replaceAll('<em>', '').replaceAll('</em>', '').replaceAll('<u>', '').replaceAll('</u>', '').replaceAll('<s>', '').replaceAll('</s>', '').trim()) || conversationId === -1}
                            loading={sending}
                            buttonStyles='primary column is-4 is-offset-4'
                            onClick={() => setSending(true)} />
                    </div>
                </div>
            </div>
        </ContentPlain>
    )
}

MessagingWindow.whyDidYouRender = false

export default MessagingWindow